import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getStoredPageSize, setStoredPageSize } from '@/components/Pagination';
import { useNavigate, useParams } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { SharedNavbar } from '@/components/SharedNavbar';
import { ToolHeader } from '@/components/ToolHeader';
import { AppShell3 } from '@/components/app-shell-3';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import {
  Calendar,
  Users,
  MapPin,
  DollarSign,
  Search,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  MoreHorizontal,
  Eye,
  Send,
  Trash2,
  Plus,
  Filter,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  Building2,
  CheckCircle,
  Clock,
  Zap,
  X
} from 'lucide-react';
import { TOOL_REGISTRY as toolConfigs } from '@/config/tools';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { PageTransition } from '@/components/PageTransition';

// Status badge component
const StatusBadge = ({ status, options_count }) => {
  const getBadgeProps = () => {
    let baseClassName = '';
    let label = '';
    let Icon = null;
    let showCount = false;

    switch (status) {
      case 'draft':
        baseClassName = 'bg-yellow-100 text-yellow-700';
        label = 'Draft';
        Icon = Clock;
        break;
      case 'live':
        baseClassName = 'bg-blue-100 text-blue-700';
        label = 'Live';
        Icon = Zap;
        showCount = options_count > 0;
        break;
      case 'booked':
        baseClassName = 'bg-green-100 text-green-700';
        label = 'Booked';
        Icon = CheckCircle;
        break;
      default:
        baseClassName = 'bg-gray-100 text-gray-700';
        label = status || 'Unknown';
        Icon = null;
    }

    return { baseClassName, label, icon: Icon, showCount };
  };

  const { baseClassName, label, icon: Icon, showCount } = getBadgeProps();

  // Determine count badge color based on baseClassName
  const getCountBadgeColor = (baseClass) => {
    if (baseClass.includes('bg-green-100')) return 'bg-green-600';
    if (baseClass.includes('bg-gray-100')) return 'bg-gray-600';
    if (baseClass.includes('bg-yellow-100')) return 'bg-yellow-600';
    if (baseClass.includes('bg-blue-100')) return 'bg-blue-600';
    if (baseClass.includes('bg-purple-100')) return 'bg-purple-600';
    if (baseClass.includes('bg-red-100')) return 'bg-red-600';
    return 'bg-gray-600'; // Default fallback
  };

  const countBadgeColor = getCountBadgeColor(baseClassName);

  return (
    <Badge variant="secondary" className={cn('text-xs', baseClassName, 'hover:' + baseClassName)}>
      <span className="flex items-center gap-1">
        {Icon && <Icon className="h-3 w-3" />}
        {label}
        {showCount && (
          <span className={cn(
            'text-white text-[10px] rounded-full px-1 min-w-[16px] h-4 flex items-center justify-center font-bold',
            countBadgeColor
          )}>
            {options_count}
          </span>
        )}
      </span>
    </Badge>
  );
};

// Sortable table header
const SortableHeader = ({ label, sortKey, currentSort, onSort }) => {
  const isSorted = currentSort.key === sortKey;
  const direction = currentSort.direction;

  return (
    <button
      onClick={() => onSort(sortKey)}
      className="flex items-center gap-1 hover:text-gray-900 transition-colors"
    >
      {label}
      {isSorted ? (
        direction === 'asc' ? (
          <ChevronUp className="h-3.5 w-3.5" />
        ) : (
          <ChevronDown className="h-3.5 w-3.5" />
        )
      ) : (
        <ChevronsUpDown className="h-3.5 w-3.5 text-gray-400" />
      )}
    </button>
  );
};

// Main BookingsTable Component
const BookingsTable = ({ stackId, stackName, stackImage, stackProjects, isStackView }) => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const { user, userProfile } = useAuth();
  usePageTitle('Bookings');

  // State
  const [initialLoading, setInitialLoading] = useState(true);
  const [space, setSpace] = useState(null);
  const [spaceTools, setSpaceTools] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filters, setFilters] = useState({
    statuses: [] // Array of selected statuses: 'draft', 'live', 'booked'
  });
  const [tempFilters, setTempFilters] = useState({
    statuses: []
  });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(() => getStoredPageSize(10));
  const [statusCounts, setStatusCounts] = useState({
    all: 0,
    draft: 0,
    live: 0,
    booked: 0,
  });

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Memoize project IDs for RPC call
  const projectIdsForQuery = useMemo(() => {
    if (isStackView && stackProjects && stackProjects.length > 0) {
      return stackProjects.map(p => p.id);
    } else if (projectId) {
      return [projectId];
    }
    return [];
  }, [isStackView, stackProjects, projectId]);

  // Single optimized fetch function using RPC
  const fetchBookingsData = useCallback(async () => {
    if (projectIdsForQuery.length === 0) {
      setInitialLoading(false);
      return;
    }

    try {
      // Use optimized RPC - single query for bookings, counts, and space info
      const { data, error } = await supabase.rpc('get_bookings_page_data', {
        p_project_ids: projectIdsForQuery,
        p_search: debouncedSearch || null,
        p_statuses: filters.statuses.length > 0 ? filters.statuses : null,
        p_sort_column: sortConfig.key,
        p_sort_direction: sortConfig.direction,
        p_page: currentPage,
        p_limit: itemsPerPage
      });

      if (error) throw error;

      // Set bookings from RPC response
      setBookings(data?.bookings || []);
      setTotalCount(data?.totalCount || 0);

      // Set status counts from RPC response (no separate query needed)
      if (data?.statusCounts) {
        setStatusCounts(data.statusCounts);
      }

      // Set space info from RPC response (for single project view)
      if (!isStackView && data?.space) {
        setSpace(data.space);
        // Process tools from the space
        if (data.space.tools) {
          const toolsObj = typeof data.space.tools === 'string'
            ? JSON.parse(data.space.tools)
            : data.space.tools;
          const tools = Object.entries(toolsObj)
            .filter(([, enabled]) => enabled === true)
            .map(([toolKey]) => ({
              id: toolKey,
              tool_type: toolKey,
              name: toolConfigs[toolKey]?.name || toolKey,
              icon: toolConfigs[toolKey]?.icon,
              color: toolConfigs[toolKey]?.color
            }));
          setSpaceTools(tools);
        }
      } else if (isStackView && stackId && stackName) {
        // For stack view, set up standard space info
        setSpace({
          id: stackId,
          name: stackName,
          type: 'Stack',
          tools: { bookings: true }
        });
        setSpaceTools([{
          id: 'bookings',
          tool_type: 'bookings',
          name: toolConfigs['bookings']?.name || 'Bookings',
          icon: toolConfigs['bookings']?.icon,
          color: toolConfigs['bookings']?.color
        }]);
      }
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast.error('Failed to load bookings');
    } finally {
      setInitialLoading(false);
    }
  }, [projectIdsForQuery, debouncedSearch, filters.statuses, sortConfig.key, sortConfig.direction, currentPage, itemsPerPage, isStackView, stackId, stackName]);

  // Fetch data when dependencies change
  useEffect(() => {
    fetchBookingsData();
  }, [fetchBookingsData]);

  // Handle sorting
  const handleSort = useCallback((key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  }, []);

  // Handle delete
  const handleDelete = useCallback(async (bookingId) => {
    if (!confirm('Are you sure you want to delete this booking?')) return;

    try {
      const { error } = await supabase
        .from('training_quotes')
        .delete()
        .eq('id', bookingId);

      if (error) throw error;

      toast.success('Booking deleted successfully');
      fetchBookingsData();
    } catch (error) {
      console.error('Error deleting booking:', error);
      toast.error('Failed to delete booking');
    }
  }, [fetchBookingsData]);

  // Handle send to CRM
  const handleSendToCRM = useCallback(async (bookingId) => {
    const booking = bookings.find(b => b.id === bookingId);
    if (!booking) return;

    try {
      toast.info('Sending quote to CRM...');

      const { error } = await supabase.functions.invoke('request-training-quote', {
        body: {
          training_quote_id: booking.id,
          course_name: booking.course_name,
          delegates: booking.delegates,
          locations: booking.locations,
          date_prefs: booking.date_prefs,
          further_info: booking.notes
        }
      });

      if (error) throw error;

      toast.success('Quote sent to CRM successfully');
      fetchBookingsData();
    } catch (error) {
      console.error('Error sending to CRM:', error);
      toast.error('Failed to send quote to CRM');
    }
  }, [bookings, fetchBookingsData]);

  // Count active filters
  const activeFilterCount = useMemo(() => {
    return filters.statuses.length;
  }, [filters.statuses]);

  // Handle filter actions
  const handleApplyFilters = useCallback(() => {
    setFilters(tempFilters);
    setFiltersOpen(false);
  }, [tempFilters]);

  const handleClearFilters = useCallback(() => {
    const clearedFilters = { statuses: [] };
    setTempFilters(clearedFilters);
    setFilters(clearedFilters);
    setFiltersOpen(false);
  }, []);

  const handleClearAllFilters = useCallback(() => {
    const clearedFilters = { statuses: [] };
    setFilters(clearedFilters);
    setTempFilters(clearedFilters);
  }, []);

  const toggleStatusFilter = useCallback((status) => {
    setTempFilters(prev => ({
      ...prev,
      statuses: prev.statuses.includes(status)
        ? prev.statuses.filter(s => s !== status)
        : [...prev.statuses, status]
    }));
  }, []);

  // Open filters popover - sync temp filters with current filters
  const handleFiltersOpen = useCallback((open) => {
    if (open) {
      setTempFilters({ ...filters });
    }
    setFiltersOpen(open);
  }, [filters]);

  // Pagination calculations (server-side)
  const totalPages = Math.ceil(totalCount / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalCount);

  // Reset to page 1 when filters or search change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, filters, sortConfig]);

  return (
    <PageTransition>
      <AppShell3
        navbar={<SharedNavbar />}
        sidebar={null}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <ToolHeader
            spaceName={space?.name}
            spaceImage={stackImage || space?.image}
            toolName="Training Bookings"
            currentTool="bookings"
            spaceTools={spaceTools}
            isStackView={isStackView}
            stackId={stackId}
            stackProjects={stackProjects}
            actions={
              !initialLoading && (
                <div className="flex items-center gap-2">
                {/* Search */}
                <div className="relative w-[300px]">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                  <Input
                    placeholder="Search bookings..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9 h-8 text-sm"
                  />
                </div>

                {/* Filters Popover */}
                <Popover open={filtersOpen} onOpenChange={handleFiltersOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 text-sm gap-1.5"
                    >
                      <Filter className="h-3.5 w-3.5" />
                      Filters
                      {activeFilterCount > 0 && (
                        <span className="ml-1 bg-primary text-primary-foreground text-[10px] rounded-full px-1.5 min-w-[18px] h-[18px] flex items-center justify-center font-bold">
                          {activeFilterCount}
                        </span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80" align="end">
                    <div className="space-y-4">
                      {/* Header */}
                      <div className="flex items-center justify-between">
                        <h4 className="font-semibold text-sm">Filters</h4>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs"
                          onClick={() => setFiltersOpen(false)}
                        >
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      </div>

                      {/* Status Filter */}
                      <div className="space-y-3">
                        <Label className="text-xs font-medium text-gray-700">Status</Label>
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="status-draft"
                              checked={tempFilters.statuses.includes('draft')}
                              onCheckedChange={() => toggleStatusFilter('draft')}
                            />
                            <label
                              htmlFor="status-draft"
                              className="text-sm font-normal cursor-pointer flex items-center justify-between flex-1"
                            >
                              <span>Draft</span>
                              <span className="text-xs text-gray-500">({statusCounts.draft})</span>
                            </label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="status-live"
                              checked={tempFilters.statuses.includes('live')}
                              onCheckedChange={() => toggleStatusFilter('live')}
                            />
                            <label
                              htmlFor="status-live"
                              className="text-sm font-normal cursor-pointer flex items-center justify-between flex-1"
                            >
                              <span>Live</span>
                              <span className="text-xs text-gray-500">({statusCounts.live})</span>
                            </label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="status-booked"
                              checked={tempFilters.statuses.includes('booked')}
                              onCheckedChange={() => toggleStatusFilter('booked')}
                            />
                            <label
                              htmlFor="status-booked"
                              className="text-sm font-normal cursor-pointer flex items-center justify-between flex-1"
                            >
                              <span>Booked</span>
                              <span className="text-xs text-gray-500">({statusCounts.booked})</span>
                            </label>
                          </div>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2 pt-2 border-t">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleClearFilters}
                          className="flex-1 h-8 text-sm"
                        >
                          Clear
                        </Button>
                        <Button
                          size="sm"
                          onClick={handleApplyFilters}
                          className="flex-1 h-8 text-sm"
                        >
                          Apply
                        </Button>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>

                {/* Clear All Filters Button */}
                {activeFilterCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleClearAllFilters}
                    className="h-8 text-sm text-gray-600 hover:text-gray-900"
                  >
                    Clear all
                  </Button>
                )}

                {/* Refresh */}
                <Button
                  variant="outline"
                  size="icon"
                  onClick={fetchBookingsData}
                  className="h-8 w-8"
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                </Button>

                {/* New Quote */}
                <Button
                  onClick={() => {
                    const currentPath = isStackView
                      ? `/stack/${stackId}/bookings`
                      : `/space/${projectId}/bookings`;
                    const buildQuotePath = isStackView
                      ? `/stack/${stackId}/build-quote?from=${encodeURIComponent(currentPath)}`
                      : `/space/${projectId}/build-quote?from=${encodeURIComponent(currentPath)}`;
                    navigate(buildQuotePath);
                  }}
                  size="sm"
                  className="h-8 text-sm"
                >
                  <Plus className="h-3.5 w-3.5 mr-1.5" />
                  New Quote
                </Button>
              </div>
              )
            }
          />

          {/* Main Content */}
          <div className="flex-1 overflow-auto p-6">
            {initialLoading ? (
              <div className="flex items-center justify-center h-full">
                <LoadingSpinner />
              </div>
            ) : (
              <div className="max-w-[1400px] mx-auto space-y-6">
                {/* Table */}
                <div className="bg-white rounded-lg border border-gray-200">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>
                        <SortableHeader
                          label="Course"
                          sortKey="course_name"
                          currentSort={sortConfig}
                          onSort={handleSort}
                        />
                      </TableHead>
                      <TableHead>
                        <SortableHeader
                          label="Provider"
                          sortKey="provider"
                          currentSort={sortConfig}
                          onSort={handleSort}
                        />
                      </TableHead>
                      <TableHead>
                        <SortableHeader
                          label="Date"
                          sortKey="scheduled_date"
                          currentSort={sortConfig}
                          onSort={handleSort}
                        />
                      </TableHead>
                      <TableHead>
                        <SortableHeader
                          label="Delegates"
                          sortKey="delegates"
                          currentSort={sortConfig}
                          onSort={handleSort}
                        />
                      </TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>
                        <SortableHeader
                          label="Amount"
                          sortKey="quote_amount"
                          currentSort={sortConfig}
                          onSort={handleSort}
                        />
                      </TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bookings.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-12">
                          <div className="flex flex-col items-center gap-2 text-gray-500">
                            <Calendar className="h-12 w-12 text-gray-300" />
                            <p className="text-sm font-medium">No bookings found</p>
                            <p className="text-xs">Try adjusting your filters or create a new booking</p>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      bookings.map((booking) => (
                        <TableRow
                          key={booking.id}
                          className="cursor-pointer"
                          onClick={() => {
                            const viewPath = isStackView
                              ? `/stack/${stackId}/bookings/${booking.id}`
                              : projectId
                              ? `/space/${projectId}/bookings/${booking.id}`
                              : `/bookings/${booking.id}`;
                            navigate(viewPath);
                          }}
                        >
                          {/* Course Name */}
                          <TableCell className="font-medium">
                            <div>
                              <div className="text-sm text-gray-900">
                                {booking.course_name}
                              </div>
                              {booking.external_quote_id && (
                                <div className="text-xs text-gray-500 mt-0.5">
                                  #{booking.external_quote_id}
                                </div>
                              )}
                            </div>
                          </TableCell>

                          {/* Provider */}
                          <TableCell>
                            <div className="flex items-center gap-1.5 text-sm text-gray-700">
                              <Building2 className="h-3.5 w-3.5 text-gray-400" />
                              {booking.provider}
                            </div>
                          </TableCell>

                          {/* Date */}
                          <TableCell>
                            <div className="flex items-center gap-1.5 text-sm text-gray-700">
                              <Calendar className="h-3.5 w-3.5 text-gray-400" />
                              {booking.scheduled_date ? (
                                format(new Date(booking.scheduled_date), 'dd MMM yyyy')
                              ) : (
                                <span className="text-gray-400">TBD</span>
                              )}
                            </div>
                          </TableCell>

                          {/* Delegates */}
                          <TableCell>
                            <div className="flex items-center gap-1.5 text-sm text-gray-700">
                              <Users className="h-3.5 w-3.5 text-gray-400" />
                              {booking.delegates}
                            </div>
                          </TableCell>

                          {/* Location */}
                          <TableCell>
                            <div className="flex items-center gap-1.5 text-sm text-gray-700 max-w-[200px]">
                              <MapPin className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                              <span className="truncate">
                                {booking.locations || 'TBD'}
                              </span>
                            </div>
                          </TableCell>

                          {/* Amount */}
                          <TableCell>
                            {booking.quote_amount ? (
                              <div className="flex items-center gap-1.5 text-sm font-semibold text-gray-900">
                                <DollarSign className="h-3.5 w-3.5 text-gray-400" />
                                £{booking.quote_amount.toLocaleString()}
                              </div>
                            ) : (
                              <span className="text-sm text-gray-400">-</span>
                            )}
                          </TableCell>

                          {/* Status */}
                          <TableCell>
                            <StatusBadge
                              status={booking.status}
                              options_count={booking.options_count}
                            />
                          </TableCell>

                          {/* Actions */}
                          <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-8 w-8">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuLabel>Actions</DropdownMenuLabel>
                                <DropdownMenuItem
                                  onClick={() => {
                                    const viewPath = isStackView
                                      ? `/stack/${stackId}/bookings/${booking.id}`
                                      : projectId
                                      ? `/space/${projectId}/bookings/${booking.id}`
                                      : `/bookings/${booking.id}`;
                                    navigate(viewPath);
                                  }}
                                >
                                  <Eye className="h-3.5 w-3.5 mr-2" />
                                  View Details
                                </DropdownMenuItem>
                                {booking.status === 'draft' && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      className="text-blue-600"
                                      onClick={() => handleSendToCRM(booking.id)}
                                    >
                                      <Send className="h-3.5 w-3.5 mr-2" />
                                      Send Requests
                                    </DropdownMenuItem>
                                  </>
                                )}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  className="text-red-600"
                                  onClick={() => handleDelete(booking.id)}
                                >
                                  <Trash2 className="h-3.5 w-3.5 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>

                {/* Pagination */}
                {totalCount > 0 && (
                  <div className="border-t border-gray-200 px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-700">
                        Showing {startIndex + 1} to {endIndex} of {totalCount} results
                      </span>
                    </div>

                    <div className="flex items-center gap-4">
                      {/* Items per page */}
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-700">Rows per page:</span>
                        <Select
                          value={itemsPerPage.toString()}
                          onValueChange={(value) => {
                            const val = Number(value);
                            setItemsPerPage(val);
                            setCurrentPage(1);
                            setStoredPageSize(val);
                          }}
                        >
                          <SelectTrigger className="w-[70px] h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="10">10</SelectItem>
                            <SelectItem value="25">25</SelectItem>
                            <SelectItem value="50">50</SelectItem>
                            <SelectItem value="100">100</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Page navigation */}
                      <div className="flex items-center gap-1">
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                          disabled={currentPage === 1}
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <span className="text-sm text-gray-700 px-3">
                          Page {currentPage} of {totalPages}
                        </span>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                          disabled={currentPage === totalPages}
                        >
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            )}
          </div>
        </div>
      </AppShell3>
    </PageTransition>
  );
};

export default BookingsTable;
